import clsx from 'clsx';
import {
  ArrowDownUp,
  ArrowLeftRight,
  BadgeDollarSign,
  Banknote,
  BarChart3,
  Bell,
  BellRing,
  Building2,
  CalendarClock,
  CalendarRange,
  ChevronsUpDown,
  Coins,
  FileText,
  Gauge,
  Handshake,
  HandCoins,
  History,
  KeyRound,
  Landmark,
  Languages,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu,
  Monitor,
  MonitorOff,
  Moon,
  Package,
  PieChart,
  PiggyBank,
  Receipt,
  Repeat,
  ScrollText,
  Scale,
  Search,
  Settings,
  ShieldAlert,
  Sun,
  TrendingUp,
  Users,
  Volume2,
  VolumeX,
  Wallet,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { relativeTime } from '../lib/format';
import { useNotificationChime, useNotificationSound } from '../lib/notification-sound';
import {
  closeShown,
  promptPutOff,
  putOffPrompt,
  turnOff,
  turnOn,
  useDesktopFromPage,
  useDesktopNotifications,
  useWorkerMessages,
  type DesktopSample,
} from '../lib/desktop-notifications';
import { VigLogo } from './Logo';
import { Button } from './ui';

interface NavItem {
  to: string;
  label: TranslationKey;
  icon: typeof Gauge;
  /** Hidden unless the user holds at least one of these. Empty = always visible. */
  permissions?: string[];
  /** Hidden unless the user holds every one of these as well. */
  requires?: string[];
}

interface NavGroup {
  label: TranslationKey | null;
  items: NavItem[];
}

/**
 * Sys's sidebar, rendered per permission. Everything here was the admin's
 * Finance section; operations stay in the admin, at its own address — the
 * sidebar deliberately has no link across to it (operator's call).
 */
const NAV: NavGroup[] = [
  {
    label: null,
    items: [{ to: '/', label: 'nav.dashboard', icon: Gauge }],
  },
  {
    label: 'nav.groupClients',
    items: [
      { to: '/clients', label: 'nav.clients', icon: Building2, permissions: [P.CLIENTS_VIEW] },
      { to: '/invoices', label: 'nav.invoices', icon: FileText, permissions: [P.INVOICES_VIEW] },
      { to: '/payments', label: 'nav.payments', icon: Banknote, permissions: [P.PAYMENTS_VIEW] },
      // Under Payments, as the operator asked: support contracts, each period invoiced by hand (2026-10-27).
      { to: '/contracts', label: 'nav.contracts', icon: Handshake, permissions: [P.CONTRACTS_VIEW] },
    ],
  },
  {
    // Subscriptions to the internal projects' packages, billed each month or year (2026-10-28).
    label: 'nav.groupSubscriptions',
    items: [
      { to: '/subscription-overview', label: 'nav.subscriptionsOverview', icon: PieChart, permissions: [P.SUBSCRIPTIONS_VIEW] },
      { to: '/subscriptions', label: 'nav.subscriptions', icon: Repeat, permissions: [P.SUBSCRIPTIONS_VIEW] },
      { to: '/subscription-packages', label: 'nav.subscriptionPackages', icon: Package, permissions: [P.SUBSCRIPTIONS_VIEW] },
      { to: '/subscription-billing', label: 'nav.subscriptionBilling', icon: CalendarClock, permissions: [P.SUBSCRIPTIONS_VIEW] },
    ],
  },
  {
    label: 'nav.groupSpending',
    items: [
      { to: '/expenses', label: 'nav.expenses', icon: Receipt, permissions: [P.FINANCE_EXPENSES_MANAGE] },
      { to: '/budgets', label: 'nav.budgets', icon: PiggyBank, permissions: [P.FINANCE_BUDGETS_MANAGE] },
    ],
  },
  {
    label: 'nav.payroll',
    items: [
      { to: '/payroll', label: 'payroll.periods', icon: Wallet, permissions: [P.PAYROLL_MANAGE] },
      { to: '/salaries', label: 'nav.salaries', icon: BadgeDollarSign, permissions: [P.PAYROLL_MANAGE] },
      // The cash employees redeemed wallet points for (CR-WALLET-001, 2026-10-20).
      { to: '/points-rewards', label: 'nav.pointsRewards', icon: Coins, permissions: [P.PAYROLL_MANAGE] },
    ],
  },
  // Finance Phase 1 (2026-10-28): the owners and their money, the months before the
  // system, and the day the books moved onto it.
  {
    label: 'nav.groupBooks',
    items: [
      { to: '/partners', label: 'nav.partners', icon: Users, permissions: [P.PARTNERS_VIEW] },
      { to: '/partner-transactions', label: 'nav.partnerTransactions', icon: ArrowLeftRight, permissions: [P.PARTNERS_VIEW] },
      { to: '/historical', label: 'nav.historical', icon: History, permissions: [P.HISTORICAL_FINANCE_VIEW] },
      { to: '/financial-setup', label: 'nav.financialSetup', icon: Landmark, permissions: [P.OPENING_BALANCES_VIEW] },
    ],
  },
  // Finance Phase 2 (2026-10-28): the financial reports. Three are built on partners'
  // money, so they need Partners access as well — the API refuses them without it.
  {
    label: 'nav.groupReports',
    items: [
      { to: '/financial-reports', label: 'nav.frOverview', icon: LayoutDashboard, permissions: [P.FINANCE_REPORTS_VIEW] },
      { to: '/financial-reports/profit-and-loss', label: 'nav.frPnl', icon: TrendingUp, permissions: [P.FINANCE_REPORTS_VIEW] },
      { to: '/financial-reports/balance-sheet', label: 'nav.frBalanceSheet', icon: Scale, permissions: [P.FINANCE_REPORTS_VIEW], requires: [P.PARTNERS_VIEW] },
      { to: '/financial-reports/cash-flow', label: 'nav.frCashFlow', icon: ArrowDownUp, permissions: [P.FINANCE_REPORTS_VIEW], requires: [P.PARTNERS_VIEW] },
      { to: '/financial-reports/partners', label: 'nav.frPartners', icon: HandCoins, permissions: [P.FINANCE_REPORTS_VIEW], requires: [P.PARTNERS_VIEW] },
      { to: '/financial-reports/monthly', label: 'nav.frMonthly', icon: CalendarRange, permissions: [P.FINANCE_REPORTS_VIEW] },
      { to: '/financial-reports/reconciliation', label: 'nav.frReconciliation', icon: ListChecks, permissions: [P.FINANCE_REPORTS_VIEW] },
    ],
  },
  {
    label: 'nav.groupAdmin',
    items: [
      { to: '/reports', label: 'nav.reports', icon: BarChart3, permissions: [P.FINANCE_REPORTS_VIEW] },
      { to: '/audit-log', label: 'nav.auditLog', icon: ScrollText, permissions: [P.FINANCE_AUDIT_VIEW] },
      { to: '/access', label: 'nav.sysAccess', icon: KeyRound, permissions: [P.SYS_ACCESS_MANAGE] },
      {
        to: '/finance/migration',
        label: 'nav.dataMigration',
        icon: ShieldAlert,
        permissions: [P.FINANCE_MIGRATION_MANAGE],
      },
      // Accountants keep the currency rates up to date; the rest of the settings are the sys admin's.
      {
        to: '/settings',
        label: 'nav.settings',
        icon: Settings,
        permissions: [P.FINANCE_SETTINGS_MANAGE, P.FINANCE_EXCHANGE_RATE_EDIT, P.EXTERNAL_SYSTEMS_MANAGE],
      },
    ],
  },
];

export function Layout({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const { can, canAny } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const visibleGroups = NAV.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) => (!item.permissions || canAny(...item.permissions)) && (!item.requires || can(...item.requires)),
    ),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="flex h-full">
      {/* Sidebar — always the dark console surface, in both themes. */}
      <aside
        data-theme="dark"
        className={clsx(
          'fixed inset-y-0 start-0 z-overlay flex w-sidebar flex-col bg-ink-deep',
          'border-e border-ink-line transition-[transform,visibility] duration-base ease-precision',
          'lg:static lg:translate-x-0',
          // The off-canvas transform is scoped to `max-lg`: `ltr:`/`rtl:` outrank the
          // `lg:` breakpoint variant, so an unscoped one would hide the desktop sidebar.
          // Closed, it is invisible too: switching the language from the top bar flips
          // it from one side to the other, and the slide would sweep across the page.
          mobileOpen
            ? 'translate-x-0'
            : 'max-lg:invisible max-lg:ltr:-translate-x-full max-lg:rtl:translate-x-full',
        )}
      >
        <div className="relative flex h-topbar items-center border-b border-ink-line px-4">
          {/* The wordmark already says “Viganium”, so the name is not repeated
              beside it — only the surface label sits underneath. */}
          <Link to="/" className="flex min-w-0 flex-1 flex-col items-center gap-1" aria-label={t('app.name')}>
            <VigLogo className="h-6 w-auto text-neutral-50" />
            <span className="block w-full truncate text-center text-mono-sm uppercase tracking-widest text-[var(--text-tertiary)]">
              {t('app.tagline')}
            </span>
          </Link>
          {/* Taken out of the flow so it cannot pull the wordmark off centre —
              it only exists below lg, where the sidebar is an overlay. */}
          <button
            type="button"
            className="absolute end-3 top-1/2 -translate-y-1/2 rounded-sm p-1 text-neutral-400 lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-2.5 py-3">
          {visibleGroups.map((group, index) => (
            <div key={group.label ?? `group-${index}`} className={index > 0 ? 'mt-5' : undefined}>
              {group.label && (
                <p className="px-2.5 pb-1.5 text-overline uppercase text-[var(--text-tertiary)]">{t(group.label)}</p>
              )}
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === '/' || item.to === '/financial-reports'}
                      className={({ isActive }) =>
                        clsx(
                          'group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-body-sm font-medium',
                          'transition-colors duration-fast ease-precision',
                          isActive
                            ? 'bg-primary/15 text-neutral-50 shadow-[inset_2px_0_0_0_#C7102E] rtl:shadow-[inset_-2px_0_0_0_#C7102E]'
                            : 'text-neutral-400 hover:bg-white/5 hover:text-neutral-100',
                        )
                      }
                    >
                      <item.icon className="h-4 w-4 shrink-0" aria-hidden />
                      <span className="truncate">{t(item.label)}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* The language moved to the top bar, and Change password and Sign out
            open from the name card, as in the admin (operator, 2026-10-10). */}
        <div className="border-t border-ink-line p-3">
          <AccountMenu />
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-sticky bg-ink-deep/60 lg:hidden" onClick={() => setMobileOpen(false)} aria-hidden />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onOpenMenu={() => setMobileOpen(true)} />
        {/* Full width by design: this is a dense operations console on desk
            monitors, and a centred column wasted ~700px a side at 2560. The
            gutters below are the only inset. */}
        <main className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">
          <DesktopPrompt />
          {children}
        </main>
      </div>
    </div>
  );
}

function TopBar({ onOpenMenu }: { onOpenMenu: () => void }) {
  const { t } = useI18n();
  const [theme, setTheme] = useState<'dark' | 'light'>(
    () => (localStorage.getItem('viganium.theme') as 'dark' | 'light' | null) ?? 'dark',
  );

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('viganium.theme', theme);
  }, [theme]);

  return (
    <header className="sticky top-0 z-sticky flex h-topbar shrink-0 items-center gap-3 border-b hairline bg-[var(--surface-card)] px-4 sm:px-6">
      <button
        type="button"
        onClick={onOpenMenu}
        className="rounded-sm p-1.5 text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] lg:hidden"
        aria-label="Open navigation"
      >
        <Menu className="h-5 w-5" />
      </button>

      <GlobalSearch />

      <div className="ms-auto flex items-center gap-1">
        <LanguageToggle />
        <button
          type="button"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          className="rounded-sm p-2 text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
          aria-label={theme === 'dark' ? t('theme.toLight') : t('theme.toDark')}
        >
          {theme === 'dark' ? <Sun className="h-4.5 w-4.5" /> : <Moon className="h-4.5 w-4.5" />}
        </button>
        <NotificationsBell />
      </div>
    </header>
  );
}

/**
 * The other language, in the top bar (operator, 2026-10-10: "move change
 * languages to top"). Named in that language; only its icon on a phone.
 */
function LanguageToggle() {
  const { locale, setLocale } = useI18n();
  const other = locale === 'en' ? 'العربية' : 'English';
  return (
    <button
      type="button"
      onClick={() => setLocale(locale === 'en' ? 'ar' : 'en')}
      lang={locale === 'en' ? 'ar' : 'en'}
      aria-label={other}
      className="flex h-9 items-center gap-1.5 rounded-sm px-2 text-body-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
    >
      <Languages className="h-4 w-4" aria-hidden />
      <span className="hidden sm:inline">{other}</span>
    </button>
  );
}

function GlobalSearch() {
  const { t, localized } = useI18n();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: ['search', query],
    queryFn: () => api.get<any>('/search', { q: query }),
    enabled: query.trim().length >= 2,
    staleTime: 10_000,
  });

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const go = (path: string) => {
    setOpen(false);
    setQuery('');
    navigate(path);
  };

  // CR-001 §55 — a company name reaches the client and its invoices. Sys
  // searches finance only; each group is empty for a caller without the
  // permission that owns it, because the server decides that, not this list.
  const groups = [
    { key: 'clients', label: t('nav.clients'), rows: data?.clients ?? [], path: (r: any) => `/clients/${r.id}`, title: (r: any) => `${r.code} — ${localized({ name: r.companyName, nameAr: r.companyNameAr })}` },
    { key: 'invoices', label: t('nav.invoices'), rows: data?.invoices ?? [], path: (r: any) => `/invoices/${r.id}`, title: (r: any) => `${r.number} — ${r.client?.companyName ?? ''}` },
    { key: 'payments', label: t('nav.payments'), rows: data?.payments ?? [], path: (r: any) => `/invoices/${r.invoice.id}`, title: (r: any) => `${r.number} → ${r.invoice.number}` },
    { key: 'contracts', label: t('nav.contracts'), rows: data?.contracts ?? [], path: (r: any) => `/contracts/${r.id}`, title: (r: any) => `${r.number} — ${r.title}` },
  ].filter((g) => g.rows.length > 0);

  return (
    <div ref={boxRef} className="relative w-full max-w-md">
      <Search
        className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-tertiary)]"
        aria-hidden
      />
      <input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={t('common.searchPlaceholder')}
        aria-label={t('common.search')}
        className="h-9 w-full rounded-md border hairline bg-[var(--surface-sunken)] ps-9 pe-3 text-body-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] dark:placeholder:text-[var(--text-secondary)] focus:border-primary"
      />

      {open && query.trim().length >= 2 && (
        <div className="absolute inset-x-0 top-full z-dropdown mt-1.5 max-h-96 overflow-y-auto rounded-md surface shadow-lg">
          {groups.length === 0 ? (
            <p className="px-3 py-4 text-body-sm text-muted">{t('common.noResults')}</p>
          ) : (
            groups.map((group) => (
              <div key={group.key} className="border-b hairline last:border-0">
                <p className="px-3 pt-2.5 pb-1 text-overline uppercase text-[var(--text-tertiary)]">{group.label}</p>
                {group.rows.map((row: any) => (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => go(group.path(row))}
                    className="block w-full truncate px-3 py-2 text-start text-body-sm text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]"
                  >
                    {group.title(row)}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

interface Notice {
  id: string;
  title: string;
  titleAr: string | null;
  body: string | null;
  bodyAr: string | null;
  /** The record's own page (`/tasks/:id`, `/leaves/:id` …); none when the reader may not open it. */
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

interface UnreadCount {
  count: number;
  latestAt: string | null;
}

function NotificationsBell() {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [sound, setSound] = useNotificationSound();
  const desktop = useDesktopNotifications(user?.id);
  const [blockedHint, setBlockedHint] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const { data: count, isFetchedAfterMount } = useQuery({
    queryKey: ['notifications', 'count'],
    queryFn: () => api.get<UnreadCount>('/notifications/unread-count'),
    refetchInterval: 30_000,
    // Keep asking while the tab is in the background: that is when the chime matters.
    refetchIntervalInBackground: true,
  });

  // A newer unread notification than the last answer is the cue to chime — and,
  // in a browser with no push service, to put it on the desktop from here.
  const latestAt = isFetchedAfterMount ? count?.latestAt : undefined;
  useNotificationChime(latestAt);
  useDesktopFromPage(latestAt, locale);

  /** A notification opened from the desktop is read, as one clicked here is. */
  const readById = (id: string) => {
    void closeShown([id]);
    void api
      .post('/notifications/mark-read', { ids: [id] })
      .catch(() => undefined)
      .finally(() => void queryClient.invalidateQueries({ queryKey: ['notifications'] }));
  };

  // The service worker: one arrived — the bell asks now; one was clicked — this page opens it.
  useWorkerMessages({
    arrived: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    opened: (id, link) => {
      if (id) readById(id);
      if (link) navigate(link);
    },
  });

  // Clicked with no page open, it opened a new one at its record, `?notice=<id>` on the address.
  const [params, setParams] = useSearchParams();
  const openedNotice = params.get('notice');
  useEffect(() => {
    if (!openedNotice) return;
    readById(openedNotice);
    const rest = new URLSearchParams(params);
    rest.delete('notice');
    setParams(rest, { replace: true });
  }, [openedNotice]);

  // Nothing unread: nothing of the bell's stays on the desktop either.
  useEffect(() => {
    if (count?.count === 0) void closeShown();
  }, [count?.count]);

  const desktopSample = (): DesktopSample => ({
    title: t('notifications.desktopSampleTitle'),
    body: t('notifications.desktopSampleBody'),
  });
  const flipDesktop = () => {
    if (desktop.state === 'blocked') setBlockedHint((v) => !v);
    else if (desktop.state === 'on') void turnOff();
    else void turnOn(desktopSample());
  };
  const desktopHint =
    desktop.state === 'blocked' && blockedHint
      ? t('notifications.desktopBlockedHint')
      : desktop.state === 'on' && desktop.mode === 'page'
        ? t('notifications.desktopPageOnly')
        : null;

  const { data: items } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => api.get<Notice[]>('/notifications'),
    enabled: open,
  });

  /**
   * Opening a notification reads it (operator, 2026-10-11: "when clicked make as
   * read"). The bell shows it read at once; the server is told, then asked again.
   */
  const markRead = (notice: Notice) => {
    if (notice.readAt) return;
    void closeShown([notice.id]);
    void queryClient.cancelQueries({ queryKey: ['notifications'] });
    const now = new Date().toISOString();
    queryClient.setQueryData<Notice[]>(['notifications', 'list'], (list) =>
      list?.map((n) => (n.id === notice.id ? { ...n, readAt: now } : n)),
    );
    queryClient.setQueryData<UnreadCount>(['notifications', 'count'], (c) => (c ? { ...c, count: Math.max(0, c.count - 1) } : c));
    void api
      .post('/notifications/mark-read', { ids: [notice.id] })
      .catch(() => undefined)
      .finally(() => void queryClient.invalidateQueries({ queryKey: ['notifications'] }));
  };

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const markAll = async () => {
    await api.post('/notifications/mark-read', {});
    void closeShown();
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const unread = count?.count ?? 0;

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-sm p-2 text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
        aria-label={`${t('notifications.title')}${unread ? ` — ${unread} ${t('notifications.unread')}` : ''}`}
      >
        <Bell className="h-4.5 w-4.5" />
        {unread > 0 && (
          <span className="absolute end-1 top-1 grid h-4 min-w-4 place-items-center rounded-pill bg-primary px-1 text-[10px] font-bold text-white tabular">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute end-0 top-full z-dropdown mt-1.5 w-80 overflow-hidden rounded-md surface shadow-lg">
          <div className="flex items-center justify-between border-b hairline px-3 py-2">
            <span className="text-title text-[var(--text-primary)]">{t('notifications.title')}</span>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <Button size="sm" variant="ghost" onClick={() => void markAll()}>
                  {t('notifications.markAllRead')}
                </Button>
              )}
              <button
                type="button"
                onClick={() => setSound(!sound)}
                aria-pressed={sound}
                aria-label={t('notifications.sound')}
                title={sound ? t('notifications.mute') : t('notifications.unmute')}
                className="rounded-sm p-1.5 text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
              >
                {sound ? <Volume2 className="h-4 w-4" aria-hidden /> : <VolumeX className="h-4 w-4" aria-hidden />}
              </button>
              {desktop.state !== 'unsupported' && (
                <button
                  type="button"
                  onClick={flipDesktop}
                  disabled={desktop.busy}
                  aria-pressed={desktop.state === 'on'}
                  aria-label={t('notifications.desktop')}
                  title={
                    desktop.state === 'on'
                      ? t('notifications.desktopTurnOff')
                      : desktop.state === 'blocked'
                        ? t('notifications.desktopBlocked')
                        : t('notifications.desktopTurnOn')
                  }
                  className="rounded-sm p-1.5 text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)] disabled:opacity-50"
                >
                  {desktop.state === 'on' ? <Monitor className="h-4 w-4" aria-hidden /> : <MonitorOff className="h-4 w-4" aria-hidden />}
                </button>
              )}
            </div>
          </div>
          {desktopHint && (
            <p className="border-b hairline bg-[var(--surface-sunken)] px-3 py-2 text-caption text-muted">{desktopHint}</p>
          )}
          <div className="max-h-96 overflow-y-auto">
            {!items?.length ? (
              <p className="px-3 py-6 text-center text-body-sm text-muted">{t('notifications.empty')}</p>
            ) : (
              items.map((n) => {
                const row = clsx(
                  'block w-full border-b hairline px-3 py-2.5 text-start last:border-0 hover:bg-[var(--surface-sunken)]',
                  !n.readAt && 'bg-primary/5',
                );
                const content = (
                  <div className="flex items-start gap-2">
                    {!n.readAt && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />}
                    <div className="min-w-0">
                      <p className="truncate text-body-sm font-medium text-[var(--text-primary)]">
                        {locale === 'ar' ? n.titleAr ?? n.title : n.title}
                      </p>
                      {(n.body || n.bodyAr) && (
                        <p className="mt-0.5 line-clamp-2 text-caption text-muted">
                          {locale === 'ar' ? n.bodyAr ?? n.body : n.body}
                        </p>
                      )}
                      <p className="mt-1 text-caption text-faint">
                        {relativeTime(n.createdAt, locale)}
                        {!n.link && ` · ${t('notifications.noPage')}`}
                      </p>
                    </div>
                  </div>
                );
                // Each one opens the record it is about. One its reader may no longer open has
                // nowhere to go: clicking it only reads it.
                return n.link ? (
                  <Link
                    key={n.id}
                    to={n.link}
                    onClick={() => {
                      markRead(n);
                      setOpen(false);
                    }}
                    className={row}
                  >
                    {content}
                  </Link>
                ) : (
                  <button key={n.id} type="button" onClick={() => markRead(n)} className={row}>
                    {content}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Offers desktop notifications in a browser that has never been asked, above the
 * page (2026-10-22). "Not now" lasts until the next sign-in; the bell's switch is
 * always there.
 */
function DesktopPrompt() {
  const { t } = useI18n();
  const { user } = useAuth();
  const desktop = useDesktopNotifications(user?.id);
  const [later, setLater] = useState(promptPutOff);

  if (!desktop.askable || later) return null;
  return (
    <div
      role="region"
      aria-label={t('notifications.desktop')}
      className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-lg border border-primary/35 bg-primary/8 px-4 py-3.5"
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/15 text-primary-600 dark:text-primary-400" aria-hidden>
        <BellRing className="h-4.5 w-4.5" />
      </span>
      <div className="min-w-0 grow basis-48">
        <p className="text-title text-[var(--text-primary)]">{t('notifications.desktopPromptTitle')}</p>
        <p className="text-body-sm text-muted">{t('notifications.desktopPromptBody')}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          icon={<Monitor className="h-4 w-4" />}
          loading={desktop.busy}
          onClick={() =>
            void turnOn({ title: t('notifications.desktopSampleTitle'), body: t('notifications.desktopSampleBody') })
          }
        >
          {t('notifications.desktopPromptOn')}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            putOffPrompt();
            setLater(true);
          }}
        >
          {t('notifications.desktopPromptLater')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Who is signed in, at the foot of the sidebar. Their account's actions open
 * from it, above the card, as in the admin (operator, 2026-10-10: "hide change
 * password and sign out under the employee name when click on name i can show
 * options").
 */
function AccountMenu() {
  const { t, locale } = useI18n();
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={boxRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={clsx(
          'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-start',
          'transition-colors duration-fast ease-precision hover:bg-white/10',
          open ? 'bg-white/10' : 'bg-white/5',
        )}
      >
        <span
          className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/25 text-caption font-semibold text-primary-300"
          aria-hidden
        >
          {initials(user?.fullName ?? '?')}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-body-sm text-neutral-100">{user?.fullName}</span>
          <span className="block truncate text-caption text-[var(--text-tertiary)]">
            {user?.roles.map((r) => (locale === 'ar' ? r.nameAr ?? r.name : r.name)).join(', ')}
          </span>
        </span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-neutral-500" aria-hidden />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute inset-x-0 bottom-full z-dropdown mb-1.5 rounded-md border border-ink-line bg-ink-card p-1.5 shadow-lg"
        >
          <Link role="menuitem" to="/account/password" onClick={() => setOpen(false)} className={ACCOUNT_ITEM}>
            <KeyRound className="h-4 w-4 shrink-0" aria-hidden />
            {t('auth.changePassword')}
          </Link>
          <button role="menuitem" type="button" onClick={() => void signOut()} className={ACCOUNT_ITEM}>
            <LogOut className="h-4 w-4 shrink-0 flip-rtl" aria-hidden />
            {t('auth.signOut')}
          </button>
        </div>
      )}
    </div>
  );
}

const ACCOUNT_ITEM =
  'flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-start text-body-sm text-neutral-300 transition-colors duration-fast hover:bg-white/5 hover:text-neutral-50';

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
